import { forwardRef, useState, useRef, useEffect } from 'react';
import { Check, CheckCheck, Globe, FileIcon, Image, Mic, Video, Download, Loader2, Play, Pause, Pencil } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { TelegramMessage } from '../types';
import { useMediaUrl, fetchMediaWithAuth } from '../hooks';
import { RenderMessageText } from '../utils';
import { VoiceWaveform } from './VoiceWaveform';
import { StickerContent } from './StickerContent';
import { ContactContent } from './ContactContent';
import { LocationContent } from './LocationContent';
import { PollContent } from './PollContent';
import { ReplyPreview } from './ReplyPreview';
import { ForwardHeader } from './ForwardHeader';
import { Reactions } from './Reactions';
import { WebPagePreview } from './WebPagePreview';
import { AudioContent } from './AudioContent';
import { MediaSpoiler } from './MediaSpoiler';
import { UserAvatar } from './UserAvatar';

interface MessageItemProps {
  message: TelegramMessage;
  chatId: number;
  isSelected: boolean;
  isRead: boolean;
  onClick: (e: React.MouseEvent) => void;
  onContextMenu: (e: React.MouseEvent) => void;
  onScrollToMessage?: (messageId: number) => void;
  showAvatar?: boolean;
  showSenderName?: boolean;
}

export const MessageItem = forwardRef<HTMLDivElement, MessageItemProps>(
  ({ message, chatId, isSelected, isRead, onClick, onContextMenu, onScrollToMessage, showAvatar = true, showSenderName = true }, ref) => {

    const formatTime = (date: Date) => {
      return new Date(date).toLocaleTimeString('ru', {
        hour: '2-digit',
        minute: '2-digit',
      });
    };

    // Determine media type
    const mediaType = message.media?.type;
    const mediaFileName = message.media?.fileName;
    const isImage = mediaType === 'photo';
    const isVideo = mediaType === 'video';
    const isGif = mediaType === 'gif' || (mediaType === 'document' && mediaFileName?.endsWith('.gif'));
    const isDocument = mediaType === 'document' && !isGif;
    const isVoice = mediaType === 'voice';
    const isVideoNote = mediaType === 'video_note';
    const isSticker = mediaType === 'sticker';
    const isAudio = mediaType === 'audio';
    const isContact = mediaType === 'contact';
    const isLocation = mediaType === 'location';
    const isVenue = mediaType === 'venue';
    const isPoll = mediaType === 'poll';

    // Проверяем, было ли сообщение реально отредактировано
    // Реакции могут устанавливать editDate, но это не редактирование контента
    // Считаем редактированием если editDate отличается от date более чем на 5 секунд
    const isActuallyEdited = message.editDate && (
      new Date(message.editDate).getTime() - new Date(message.date).getTime() > 5000
    );

    // Spoiler state
    const [spoilerRevealed, setSpoilerRevealed] = useState(false);
    const hasSpoiler = message.media?.isSpoiler && !spoilerRevealed;

    // Load media for photo, gif, voice, video note, video
    const shouldLoadMedia = mediaType && (isImage || isGif || isVoice || isVideoNote || isVideo);
    const { url: mediaUrl, isLoading: isMediaLoading, error: mediaError } = useMediaUrl(
      shouldLoadMedia ? chatId : null,
      shouldLoadMedia ? message.id : null
    );

    // Audio/video player state
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const audioRef = useRef<HTMLAudioElement>(null);
    const videoRef = useRef<HTMLVideoElement>(null);

    // Audio player handlers
    const handleAudioPlayPause = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (audioRef.current) {
        if (isPlaying) {
          audioRef.current.pause();
        } else {
          audioRef.current.play();
        }
        setIsPlaying(!isPlaying);
      }
    };

    // Video player handlers
    const handleVideoPlayPause = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (videoRef.current) {
        if (isPlaying) {
          videoRef.current.pause();
        } else {
          videoRef.current.play();
        }
        setIsPlaying(!isPlaying);
      }
    };

    // Format duration
    const formatDuration = (seconds: number) => {
      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    // Reset state on message change
    useEffect(() => {
      setIsPlaying(false);
      setCurrentTime(0);
      setDuration(0);
      setSpoilerRevealed(false);
    }, [message.id]);

    const handleDownload = async (e: React.MouseEvent) => {
      e.stopPropagation();
      const blob = await fetchMediaWithAuth(chatId, message.id);
      if (blob) {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = mediaFileName || 'file';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    };

    // Handle reply click - scroll to message
    const handleReplyClick = () => {
      if (message.replyToMessage?.id && onScrollToMessage) {
        onScrollToMessage(message.replyToMessage.id);
      }
    };

    // Determine if message has only sticker (no bubble needed)
    const isStickerOnly = isSticker && !message.text;

    return (
      <div
        ref={ref}
        onClick={onClick}
        onContextMenu={onContextMenu}
        className={cn(
          'group relative flex items-end gap-2 mb-1 px-4 py-0.5 transition-colors',
          message.isOutgoing ? 'justify-end' : 'justify-start',
          isSelected && 'bg-primary/10'
        )}
      >
        {/* Avatar for incoming messages */}
        {!message.isOutgoing && message.fromName && showAvatar && (
          <div className="flex-shrink-0 mb-1">
            <UserAvatar
              userId={message.fromId}
              name={message.fromName}
              className="w-8 h-8"
            />
          </div>
        )}

        {/* Space for avatar when not showing it */}
        {!message.isOutgoing && message.fromName && !showAvatar && (
          <div className="flex-shrink-0 w-8" />
        )}

        {/* Selection indicator */}
        {isSelected && (
          <div className={cn(
            'absolute z-10',
            message.isOutgoing ? '-left-8 top-1/2 -translate-y-1/2' : 'left-2 top-1/2 -translate-y-1/2'
          )}>
            <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center shadow-sm">
              <Check className="h-4 w-4 text-primary-foreground" />
            </div>
          </div>
        )}

        <div
          className={cn(
            'max-w-[70%] rounded-2xl cursor-pointer transition-all overflow-hidden',
            // No padding for media-only messages
            (isImage || isVideo || isGif || isVideoNote || isStickerOnly) ? 'p-0' : 'px-3 py-2',
            // Video notes and stickers without background
            (isVideoNote || isStickerOnly)
              ? 'bg-transparent'
              : (message.isOutgoing
                ? 'bg-primary text-primary-foreground rounded-br-md'
                : 'bg-muted rounded-bl-md'),
            // Selection shadow
            isSelected && !isVideoNote && !isStickerOnly && 'shadow-[0_0_0_2px_hsl(var(--primary))]'
          )}
        >
          {/* Forward header */}
          {message.forwardInfo && (
            <div className={cn((isImage || isVideo || isGif) && 'px-3 pt-2')}>
              <ForwardHeader forwardInfo={message.forwardInfo} isOutgoing={message.isOutgoing} />
            </div>
          )}

          {/* Reply preview */}
          {message.replyToMessage && (
            <div className={cn((isImage || isVideo || isGif) && 'px-3 pt-2')}>
              <ReplyPreview
                replyToMessage={message.replyToMessage}
                isOutgoing={message.isOutgoing}
                onClick={handleReplyClick}
              />
            </div>
          )}

          {/* Sender name for incoming messages */}
          {!message.isOutgoing && message.fromName && !isStickerOnly && showSenderName && (
            <p className={cn(
              'text-xs font-medium text-primary mb-1',
              (isImage || isVideo || isGif) && 'px-3 pt-2'
            )}>
              {message.fromName}
            </p>
          )}

          {/* Photo content */}
          {isImage && (
            <div className="relative">
              {isMediaLoading && (
                <div className="w-full h-48 flex items-center justify-center bg-muted/50">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              )}
              {mediaUrl && !mediaError && (
                hasSpoiler ? (
                  <MediaSpoiler onReveal={() => setSpoilerRevealed(true)}>
                    <img
                      src={mediaUrl}
                      alt=""
                      className={cn(
                        'max-w-full max-h-80 object-contain',
                        isMediaLoading && 'hidden'
                      )}
                    />
                  </MediaSpoiler>
                ) : (
                  <img
                    src={mediaUrl}
                    alt=""
                    className={cn(
                      'max-w-full max-h-80 object-contain',
                      isMediaLoading && 'hidden'
                    )}
                  />
                )
              )}
              {mediaError && (
                <div className="flex items-center gap-2 px-3 py-2">
                  <Image className="h-4 w-4 opacity-75" />
                  <span className="text-sm italic opacity-75">Фото</span>
                </div>
              )}
            </div>
          )}

          {/* GIF content (stored as mp4 in Telegram) */}
          {isGif && (
            <div className="relative">
              {isMediaLoading && (
                <div className="w-full h-48 flex items-center justify-center bg-muted/50">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              )}
              {mediaUrl && !mediaError && (
                hasSpoiler ? (
                  <MediaSpoiler onReveal={() => setSpoilerRevealed(true)}>
                    <video
                      src={mediaUrl}
                      className="max-w-full max-h-80 object-contain"
                      autoPlay
                      loop
                      muted
                      playsInline
                    />
                  </MediaSpoiler>
                ) : (
                  <video
                    src={mediaUrl}
                    className="max-w-full max-h-80 object-contain"
                    autoPlay
                    loop
                    muted
                    playsInline
                    onClick={(e) => e.stopPropagation()}
                  />
                )
              )}
              {mediaError && !isMediaLoading && (
                <div className="flex items-center gap-2 px-3 py-2">
                  <Play className="h-4 w-4 opacity-75" />
                  <span className="text-sm italic opacity-75">GIF</span>
                </div>
              )}
            </div>
          )}

          {/* Video content */}
          {isVideo && (
            <div className="relative">
              {isMediaLoading && (
                <div className="w-full h-48 flex items-center justify-center bg-black/10">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              )}
              {mediaUrl && !mediaError && (
                hasSpoiler ? (
                  <MediaSpoiler onReveal={() => setSpoilerRevealed(true)}>
                    <video
                      ref={videoRef}
                      src={mediaUrl}
                      className="max-w-full max-h-80 object-contain"
                      controls
                      playsInline
                      preload="metadata"
                    />
                  </MediaSpoiler>
                ) : (
                  <video
                    ref={videoRef}
                    src={mediaUrl}
                    className="max-w-full max-h-80 object-contain"
                    controls
                    playsInline
                    preload="metadata"
                    onClick={(e) => e.stopPropagation()}
                  />
                )
              )}
              {mediaError && !isMediaLoading && (
                <div className="flex items-center gap-2 px-3 py-2">
                  <Video className="h-4 w-4 opacity-75" />
                  <span className="text-sm italic opacity-75">Видео</span>
                </div>
              )}
              {!mediaUrl && !isMediaLoading && !mediaError && (
                <div className="w-full h-48 flex items-center justify-center bg-black/10">
                  <Video className="h-16 w-16 text-muted-foreground/50" />
                </div>
              )}
            </div>
          )}

          {/* Sticker */}
          {isSticker && message.media && (
            <StickerContent
              media={message.media}
              chatId={chatId}
              messageId={message.id}
            />
          )}

          {/* Voice message */}
          {isVoice && (
            <div className="flex items-center gap-2 py-1 min-w-[200px]">
              {isMediaLoading ? (
                <>
                  <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                    <Loader2 className="h-5 w-5 animate-spin" />
                  </div>
                  <div className="flex-1 h-[23px] bg-primary/10 rounded animate-pulse" />
                  <span className="text-xs opacity-75">--:--</span>
                </>
              ) : (mediaUrl && !mediaError ? (
                <>
                  <audio
                    ref={audioRef}
                    src={mediaUrl}
                    onTimeUpdate={() => setCurrentTime(audioRef.current?.currentTime || 0)}
                    onLoadedMetadata={() => setDuration(audioRef.current?.duration || 0)}
                    onEnded={() => setIsPlaying(false)}
                    onPlay={() => setIsPlaying(true)}
                    onPause={() => setIsPlaying(false)}
                  />
                  <button
                    onClick={handleAudioPlayPause}
                    className={cn(
                      'w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-colors',
                      message.isOutgoing
                        ? 'bg-black/30 hover:bg-black/40 text-black'
                        : 'bg-foreground/90 text-background hover:bg-foreground'
                    )}
                  >
                    {isPlaying ? (
                      <Pause className="h-5 w-5" />
                    ) : (
                      <Play className="h-5 w-5 ml-0.5" />
                    )}
                  </button>
                  <VoiceWaveform
                    waveformData={message.media?.waveform}
                    duration={message.media?.duration || duration || 1}
                    progress={duration > 0 ? currentTime / duration : 0}
                    isOutgoing={message.isOutgoing}
                    onSeek={(progress) => {
                      if (audioRef.current && duration > 0) {
                        audioRef.current.currentTime = progress * duration;
                      }
                    }}
                    className="flex-1"
                  />
                  <span className="text-xs opacity-75 min-w-[32px] text-right">
                    {formatDuration(isPlaying ? currentTime : (message.media?.duration || duration))}
                  </span>
                </>
              ) : (
                <>
                  <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                    <Mic className="h-5 w-5 opacity-50" />
                  </div>
                  <span className="text-sm italic opacity-75">Голосовое сообщение</span>
                </>
              ))}
            </div>
          )}

          {/* Video note (circle video) */}
          {isVideoNote && (
            <div className="flex flex-col items-center">
              <div className="relative w-52 h-52">
                {isMediaLoading ? (
                  <div className="w-full h-full rounded-full bg-black/10 flex items-center justify-center overflow-hidden">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                  </div>
                ) : (mediaUrl && !mediaError ? (
                  <>
                    <video
                      ref={videoRef}
                      src={mediaUrl}
                      className="w-full h-full rounded-full object-cover"
                      loop
                      playsInline
                      onTimeUpdate={() => setCurrentTime(videoRef.current?.currentTime || 0)}
                      onLoadedMetadata={() => setDuration(videoRef.current?.duration || 0)}
                      onPlay={() => setIsPlaying(true)}
                      onPause={() => setIsPlaying(false)}
                      onClick={handleVideoPlayPause}
                    />
                    {!isPlaying && (
                      <div
                        className="absolute inset-0 flex items-center justify-center cursor-pointer"
                        onClick={handleVideoPlayPause}
                      >
                        <div className="w-12 h-12 rounded-full bg-black/50 flex items-center justify-center">
                          <Play className="h-6 w-6 text-white ml-0.5" />
                        </div>
                      </div>
                    )}
                    {/* Circular progress bar */}
                    <svg
                      className="absolute inset-0 w-full h-full -rotate-90 pointer-events-none"
                      viewBox="0 0 100 100"
                    >
                      <circle
                        cx="50"
                        cy="50"
                        r="49"
                        fill="none"
                        stroke="rgba(255,255,255,0.3)"
                        strokeWidth="2"
                      />
                      <circle
                        cx="50"
                        cy="50"
                        r="49"
                        fill="none"
                        stroke="white"
                        strokeWidth="2"
                        strokeDasharray={`${duration > 0 ? (currentTime / duration) * 308 : 0} 308`}
                        strokeLinecap="round"
                      />
                    </svg>
                  </>
                ) : (
                  <div className="w-full h-full rounded-full bg-black/10 flex items-center justify-center overflow-hidden">
                    <Video className="h-12 w-12 text-muted-foreground/50" />
                  </div>
                ))}
              </div>
              {/* Time and status under video note */}
              <div className="flex items-center gap-1 mt-1 text-muted-foreground text-[10px]">
                {message.isPublic && <Globe className="h-3 w-3" />}
                {isActuallyEdited && <Pencil className="h-2.5 w-2.5" />}
                <span>{formatTime(message.date)}</span>
                {message.isOutgoing && (
                  isRead ? (
                    <CheckCheck className="h-3 w-3 text-blue-400" />
                  ) : (
                    <Check className="h-3 w-3" />
                  )
                )}
              </div>
            </div>
          )}

          {/* Audio (music) */}
          {isAudio && message.media && (
            <AudioContent
              media={message.media}
              chatId={chatId}
              messageId={message.id}
              isOutgoing={message.isOutgoing}
            />
          )}

          {/* Contact */}
          {isContact && message.media?.contact && (
            <ContactContent
              contact={message.media.contact}
              isOutgoing={message.isOutgoing}
            />
          )}

          {/* Location/Venue */}
          {(isLocation || isVenue) && (
            <LocationContent
              location={message.media?.location}
              venue={message.media?.venue}
              isOutgoing={message.isOutgoing}
            />
          )}

          {/* Poll */}
          {isPoll && message.media?.poll && (
            <PollContent
              poll={message.media.poll}
              isOutgoing={message.isOutgoing}
            />
          )}

          {/* Document */}
          {isDocument && (
            <div className="flex items-center gap-3 py-1">
              <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
                <FileIcon className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">
                  {mediaFileName || 'Документ'}
                </p>
                <p className="text-xs opacity-75">
                  {message.media?.fileSize ? formatFileSize(message.media.fileSize) : 'Документ'}
                </p>
              </div>
              <button
                onClick={handleDownload}
                className="p-2 hover:bg-black/10 rounded-full transition-colors"
              >
                <Download className="h-4 w-4" />
              </button>
            </div>
          )}

          {/* WebPage preview */}
          {message.webPage && !isImage && !isVideo && (
            <WebPagePreview
              webPage={message.webPage}
              isOutgoing={message.isOutgoing}
              className="mt-1"
            />
          )}

          {/* Message text */}
          {message.text && (
            <div className={cn(
              'whitespace-pre-wrap break-words text-sm',
              (isImage || isVideo || isGif) && 'px-3 pb-1 pt-2'
            )}>
              <RenderMessageText
                text={message.text}
                entities={message.entities}
                isOutgoing={message.isOutgoing}
              />
            </div>
          )}

          {/* Reactions */}
          {message.reactions && message.reactions.length > 0 && !isVideoNote && !isStickerOnly && (
            <div className={cn((isImage || isVideo || isGif) && 'px-3 pb-1')}>
              <Reactions reactions={message.reactions} isOutgoing={message.isOutgoing} />
            </div>
          )}

          {/* Message footer (hidden for video notes and sticker-only) */}
          {!isVideoNote && !isStickerOnly && (
            <div
              className={cn(
                'flex items-center justify-end gap-1 mt-1 -mb-1',
                message.isOutgoing ? 'text-primary-foreground/70' : 'text-muted-foreground',
                (isImage || isVideo || isGif) && 'px-3 pb-2'
              )}
            >
              {/* Views count for channels */}
              {message.views && message.views > 0 && (
                <span className="text-[10px]">{formatViews(message.views)}</span>
              )}

              {/* Public badge */}
              {message.isPublic && (
                <Globe className="h-3 w-3" />
              )}

              {/* Edited indicator */}
              {isActuallyEdited && (
                <Pencil className="h-2.5 w-2.5" />
              )}

              {/* Time */}
              <span className="text-[10px]">
                {formatTime(message.date)}
              </span>

              {/* Read status for outgoing messages */}
              {message.isOutgoing && (
                isRead ? (
                  <CheckCheck className="h-3 w-3 text-blue-400" />
                ) : (
                  <Check className="h-3 w-3" />
                )
              )}
            </div>
          )}

          {/* Reactions for sticker-only messages (outside bubble) */}
          {isStickerOnly && message.reactions && message.reactions.length > 0 && (
            <Reactions reactions={message.reactions} isOutgoing={message.isOutgoing} />
          )}
        </div>

        {/* Sticker-only footer (outside bubble) */}
        {isStickerOnly && (
          <div className="flex items-center gap-1 mt-1 text-muted-foreground text-[10px] absolute bottom-1 right-5">
            {message.isPublic && <Globe className="h-3 w-3" />}
            {isActuallyEdited && <Pencil className="h-2.5 w-2.5" />}
            <span>{formatTime(message.date)}</span>
            {message.isOutgoing && (
              isRead ? (
                <CheckCheck className="h-3 w-3 text-blue-400" />
              ) : (
                <Check className="h-3 w-3" />
              )
            )}
          </div>
        )}
      </div>
    );
  }
);

MessageItem.displayName = 'MessageItem';

function formatFileSize(bytes: number): string {
  if (bytes < 1024) {return `${bytes} B`;}
  if (bytes < 1024 * 1024) {return `${(bytes / 1024).toFixed(1)} KB`;}
  if (bytes < 1024 * 1024 * 1024) {return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;}
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

function formatViews(views: number): string {
  if (views < 1000) {return views.toString();}
  if (views < 1000000) {return `${(views / 1000).toFixed(1)}K`;}
  return `${(views / 1000000).toFixed(1)}M`;
}
